import React, { useState, useEffect } from 'react';
import { Award, BookOpen, Briefcase, GraduationCap, Users } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const DEFAULT_PHOTO = "https://customer-assets.emergentagent.com/job_jamaica-law-firm/artifacts/mv5esbh0_image.png";

const defaultCredentials = [
  { degree: "BBA in Human Resources and Marketing", institution: "University of Technology (UTech)" },
  { degree: "LLB (Bachelor of Laws)", institution: "University of London" },
  { degree: "Legal Education Certificate", institution: "Norman Manley Law School" },
  { degree: "Certificate in International Maritime Law", institution: "CMU" },
  { degree: "MBA in International Business", institution: "Mona School of Business and Management" }
];

const defaultAffiliations = [
  "St George's College Alumni",
  "University of London Alumni",
  "Mona School of Business and Management Alumni",
  "Past Vice-Chairman, Donald Quarrie High School Board of Governors"
];

const defaultExperienceAreas = ['Banking', 'Marketing', 'Sales', 'Manufacturing', 'Operations', 'Logistics'];

export const AboutPage = () => {
  const [content, setContent] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const cacheBust = `?_t=${Date.now()}`;
        const response = await axios.get(`${API}/content/about${cacheBust}`);
        setContent(response.data);
      } catch (error) {
        console.error('Error fetching content:', error);
      }
    };
    fetchContent();
  }, []);

  const credentials = content?.credentials || defaultCredentials;
  const affiliations = content?.affiliations || defaultAffiliations;
  const experienceAreas = content?.experience_areas || defaultExperienceAreas;

  return (
    <div className="p-8 md:p-12 lg:p-16">
      {/* Header */}
      <div className="text-center mb-12" data-testid="about-header">
        <p className="font-body text-xs uppercase tracking-[0.2em] text-[#C9A86C] mb-2">About The Firm</p>
        <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#1a1412]">
          Meet {content?.name || "Devon Brooks"}
        </h1>
      </div>

      {/* Main Bio Section */}
      <section className="grid lg:grid-cols-2 gap-12 mb-16" data-testid="bio-section">
        <div className="relative">
          <div className="relative rounded-xl overflow-hidden shadow-2xl">
            <img 
              src={content?.photo_url || DEFAULT_PHOTO}
              alt={content?.name || "Devon Brooks"}
              className="w-full max-w-lg mx-auto"
              data-testid="about-devon-photo"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#1a1412] to-transparent p-6">
              <p className="font-heading text-xl text-white font-semibold">{content?.name || "Devon Brooks"}</p>
              <p className="text-[#C9A86C] text-sm tracking-wider uppercase">Attorney-at-Law</p>
            </div>
          </div>
          <div className="absolute -bottom-4 -right-4 hidden lg:block">
            <div className="stamp bg-[#f5f0e8] px-4 py-2 rounded">
              <span className="text-sm font-semibold">23+ Years</span>
            </div>
          </div>
        </div>
        <div>
          <h2 className="font-heading text-3xl font-semibold text-[#1a1412] mb-2">
            {content?.name || "Devon Brooks"}
          </h2>
          <p className="font-body uppercase !text-sm !tracking-[0.2em] mb-2 !text-[#624103] mb-6 font-semibold">
            {content?.title || "Attorney-at-Law | Managing Partner"}
          </p>
          <div className="font-body text-[#5a4a3a] leading-relaxed space-y-4">
            {(content?.bio || "His professional experience and training spans over 23 years in Banking, Marketing, Sales, LPG and Industrial Gases Industry, Manufacturing, Operations and Logistics. Devon holds a BBA in Human Resources and Marketing (UTech), an LLB from the University of London, and a Legal Education Certificate from Norman Manley Law School; a Certificate in International Maritime Law (CMU), and an MBA in International Business (MSBM). He is also the Past Vice-Chairman of Donald Quarrie High School's Board of Governors, currently serving as company secretary and director on a number of private sector organisations. Devon is a proud St George's College 'Old Boy' and a part of the University of London Alumni, as well as the Mona School of Business and Management (MSBM) Alumni.").split('\n').map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Education & Credentials */}
      <section className="mb-16" data-testid="credentials-section">
        <div className="text-center mb-10">
          <GraduationCap className="mx-auto text-[#C9A86C] mb-4" size={40} />
          <h2 className="font-heading text-3xl font-semibold text-[#1a1412]">
            Education & Credentials
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {credentials.map((cred, index) => (
            <div 
              key={index}
              className="bg-white p-6 rounded-xl border border-[#C9A86C]/20 shadow-sm hover:shadow-lg transition-all hover:border-[#C9A86C]/40"
              data-testid={`credential-${index}`}
            >
              <BookOpen className="text-[#C9A86C] mb-3" size={24} />
              <h3 className="font-heading font-semibold text-[#1a1412] mb-1">{cred.degree}</h3>
              <p className="font-body text-sm text-[#7a7065]">{cred.institution}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Experience Areas */}
      <section className="mb-16" data-testid="experience-section">
        <div className="bg-[#1a1412] rounded-xl p-8 md:p-12">
          <div className="text-center mb-8">
            <Briefcase className="mx-auto text-[#C9A86C] mb-4" size={40} />
            <h2 className="font-heading text-3xl font-semibold text-white">
              Professional Experience
            </h2>
            <p className="font-body text-white/60 mt-2">
              A multi-disciplinary background spanning 23+ years
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {experienceAreas.map((area, index) => (
              <div key={index} className="text-center p-4 bg-white/5 rounded-lg border border-[#C9A86C]/20 hover:border-[#C9A86C]/50 transition-colors">
                <p className="font-heading text-white font-medium">{area}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Affiliations */}
      <section className="mb-16" data-testid="affiliations-section">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <Users className="text-[#C9A86C] mb-4" size={40} />
            <h2 className="font-heading text-3xl font-semibold text-[#1a1412] mb-6">
              Affiliations & Service
            </h2>
            <ul className="space-y-4">
              {affiliations.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <Award className="text-[#C9A86C] flex-shrink-0 mt-1" size={20} />
                  <span className="font-body text-[#5a4a3a]">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative rounded-xl overflow-hidden shadow-lg">
            <img 
              src="https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?auto=format&fit=crop&q=80&w=1000"
              alt="Law Library"
              className="w-full h-72 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1a1412]/60 to-transparent" />
          </div>
        </div>
      </section>

      {/* Current Roles */}
      <section data-testid="roles-section">
        <div className="bg-[#f5f0e8] rounded-xl p-8 text-center border border-[#C9A86C]/20">
          <h2 className="font-heading text-2xl font-semibold text-[#1a1412] mb-4">
            Current Roles
          </h2>
          <p className="font-body text-[#5a4a3a] max-w-3xl mx-auto">
            {content?.current_roles || "Currently serving as company secretary and director on a number of private sector organisations, bringing legal expertise and business acumen to corporate governance."}
          </p>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
